import { Box, Stack, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"

import { formatRelativeDate } from "@ggs/utils"
import type { LinkType } from "../../graphql/types"
import type { FC, FCWC, ResponsiveImageType, TagProps } from ".."
import { Breadcrumbs, SvgIcon, Tags, WishlistButton } from ".."
import HeroMedia from "./HeroMedia"

type Hero3Props = {
  title: string
  description?: string
  tags: TagProps[]
  image?: ResponsiveImageType
  duration?: string
  date?: string
  path?: LinkType[]
  padBottom?: boolean
  location?: string
  onLike?: (arg?: { contentType?: string; nid?: number }, arg2?: string) => void
  onUnlike?: (arg?: { contentType?: string; nid?: number }, arg2?: string) => void
  isArticleLiked?: boolean
}

const Hero3: FC<Hero3Props> = ({
  title,
  description,
  image,
  tags,
  path,
  duration,
  date,
  padBottom = false,
  location,
  onLike,
  onUnlike,
  isArticleLiked,
}) => {
  return (
    <HeroContainer>
      <HeroInner padBottom={padBottom}>
        {image ? (
          <HeroMedia media={[image]} title={location} delay />
        ) : (
          <Box
            bgcolor={(theme) => theme.palette["tertiary.300"].main}
            position="absolute"
            top="0"
            bottom="0"
            left="0"
            right="0"
          />
        )}
        {path && path.length > 0 && (
          <Box mb="auto" mt={{ xs: 16, md: 0 }} position="relative">
            <Breadcrumbs items={path} color={image ? "dark" : "light"} />
          </Box>
        )}
        <HeroContent>
          <HeroTags
            tags={tags}
            onLike={onLike ? onLike : () => {}}
            onUnlike={onUnlike ? onUnlike : () => {}}
            isArticleLiked={isArticleLiked}
          />
          <Stack rowGap={{ xs: 3, md: 5 }} mt="auto" px={{ xs: 5, md: 10 }} py={{ xs: 10, md: 15 }}>
            <HeroTitle title={title} />
            <HeroDescription description={description} />
          </Stack>
          {duration && date && (
            <Stack
              direction="row"
              py={3}
              mx={{ xs: 5, md: 10 }}
              borderTop={(theme) => `1px solid ${alpha(theme.palette["neutral.100"].main, 0.1)}`}
            >
              <HeroDuration duration={duration} />
              <HeroPublishedDate date={date} />
            </Stack>
          )}
        </HeroContent>
      </HeroInner>
    </HeroContainer>
  )
}
export default Hero3

/** A sub-component for the section container of the hero content */
const HeroContainer: FCWC = ({ children, ...props }, ref) => (
  <Stack
    component="header"
    alignItems="stretch"
    justifyContent="flex-start"
    width="100%"
    position="relative"
    height={{ xs: 644, md: 783 }}
    pt={{ xs: 0, md: 32.5 }}
    bgcolor={(theme) => theme.palette["tertiary.200"].main}
    {...props}
  >
    {children}
  </Stack>
)

/** A sub-component for the section container of the hero content */
const HeroInner: FCWC<{ padBottom: boolean }> = ({ padBottom, children }) => (
  <Stack
    height={{ xs: "100%", md: 653 }}
    px={{ md: 10 }}
    sx={{
      isolation: "isolate",
      position: "relative",
      ...(padBottom && {
        pb: { md: 10 },
      }),
    }}
  >
    <Stack
      justifyContent="flex-end"
      height="100%"
      position="relative"
      sx={{ borderRadius: { md: 1.5 }, overflow: "hidden" }}
    >
      {children}
    </Stack>
  </Stack>
)

/** A sub-component for the content of the hero content */
const HeroContent: FCWC = ({ children }) => (
  <Stack alignItems="flex-start" position="relative" zIndex={3} p={{ xs: 3, md: 10 }}>
    <Stack
      width="100%"
      maxWidth={{ md: 591 }}
      minHeight={{ xs: "auto", md: 324 }}
      bgcolor={(theme) => alpha(theme.palette["secondary.800"].main, 0.8)}
      borderRadius={1.5}
      boxShadow={(theme) => `0 0 8px ${alpha(theme.palette["secondary.800"].main, 0.1)}`}
      color={(theme) => theme.palette["neutral.100"].main}
      sx={{ backdropFilter: "blur(3px)" }}
    >
      {children}
    </Stack>
  </Stack>
)

/** A sub-component for the tags content of the hero content */
const HeroTags: FC<Pick<Hero3Props, "tags" | "onLike" | "onUnlike" | "isArticleLiked">> = ({
  tags,
  onLike,
  onUnlike,
  isArticleLiked,
}) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    py={{ xs: 3.5, md: 4.5 }}
    px={{ xs: 5, md: 10 }}
    borderBottom={(theme) => `1px solid ${alpha(theme.palette["neutral.100"].main, 0.1)}`}
  >
    {tags.length > 0 && <Tags tags={tags} columnGap={3} autoWidth />}
    <WishlistButton onLike={onLike} onUnlike={onUnlike} liked={isArticleLiked} />
  </Stack>
)

/** A sub-component for the title content of the hero content */
const HeroTitle: FC<Pick<Hero3Props, "title">> = ({ title }) => (
  <Typography
    variant="heading.xl"
    component="h1"
    color={(theme) => theme.palette["neutral.100"].main}
  >
    {title}
  </Typography>
)

/** A sub-component for the description content of the hero content */
const HeroDescription: FC<Pick<Hero3Props, "description">> = ({ description }) => (
  <>
    {description && (
      <Typography
        variant="body.md"
        component="div"
        color={(theme) => theme.palette["neutral.100"].main}
        dangerouslySetInnerHTML={{ __html: description }}
      />
    )}
  </>
)

/** A sub-component for the duration content of the hero content */
const HeroDuration: FC<Pick<Hero3Props, "duration">> = ({ duration }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={3}
    py={3}
    pl={{ xs: 0, md: 5 }}
    pr={{ xs: 5, md: 10 }}
    borderRight={(theme) => `1px solid ${alpha(theme.palette["neutral.100"].main, 0.1)}`}
  >
    <SvgIcon variant="clock" size={{ xs: 14, md: 16 }} />
    <Typography variant="body.sm" fontWeight={500}>
      {duration}
    </Typography>
  </Stack>
)

/** A sub-component for the price content of the hero content */
const HeroPublishedDate: FC<Pick<Hero3Props, "date">> = ({ date }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={3}
    flex="1 1 auto"
    py={3}
    pr={{ xs: 0, md: 5 }}
    pl={{ xs: 5, md: 10 }}
  >
    <Typography variant="body.sm">
      {`Published on ${formatRelativeDate(date, "MMM DD, YYYY")}`}
    </Typography>
  </Stack>
)
